<template>
  <o-loading :full-page="true" :active="isLoading" :can-cancel="false"></o-loading>
  <div class="list-tab has-radius-top-left has-background-white">
    <div class="column">
      <div class="columns">
        <div class="column">
          <div class="box has-background-grey-lighter border-radius">
            <i
              @click="togglePassword"
              class="button is-pulled-right is-small p-0 has-background-white has-text-black-bis"
              :class="!showPassword ? 'icon-show' : 'icon-hide disabled-anchor-tag'"
            ></i>
            <template v-for="field in credentialFields" :key="field.key">
              <h3 class="is-size-3 has-text-black-bis has-text-weight-semibold line-height">{{ field.label }}</h3>
              <p class="line-height has-text-weight-normal has-text-black-bis">{{ showPassword ? credentials[field.key] : maskedValue }}</p>
            </template>
          </div>
        </div>
        <div class="column">
          <div class="box has-background-grey-lighter border-radius">
            <h3 class="title is-size-3 has-text-black-bis">Not Subscribed Tables</h3>
            <p class="line-height has-text-weight-normal has-text-black-bis" v-for="(value, key) in unSubscribedTables" :key="key">
              {{ value }}
            </p>
          </div>
        </div>
        <div class="column">
          <div class="box has-background-grey-lighter border-radius">
            <h3 class="title is-size-3 has-text-black-bis has-text-weight-semibold">Subscribed Tables</h3>
            <p class="line-height has-text-weight-normal has-text-black-bis" v-for="(option, index) in credentials.tables" :key="index">
              {{ tablesMap[option.name] }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { cloneDeep } from 'lodash';
import { useToast } from 'vue-toastification';
import SqlCreds from '@/models/SqlCreds';
import { data } from 'jquery';

export default {
  name: 'Analytics',
  setup() {
    const store = useStore();
    const toast = useToast();

    const credentials = ref({ tables: [] });
    const tablesMap = {
      material_orders: 'Material Orders',
      production_orders: 'Production Orders',
      shipping_orders: 'Shipping Orders',
      production_runs_items: 'Productivity Runs & Items',
      material_items: 'Material Items',
      catalog_parts: 'Catalog Parts',
      catalog_assemblies: 'Catalog Assemblies',
      forms: 'Forms Table',
      schedules: 'Schedule',
      inventory: 'Inventory',
      locations: 'Locations',
      shipping_items: 'Shipping Items Table',
      qaQcForms: 'qaQc Forms Table',
      projects: 'Projects',
      material_requisition: 'Materials Requisition',
      weld_information: 'Weld Information',
    };
    const credentialFields = [
      { key: 'host', label: 'Host/Url' },
      { key: 'user', label: 'Username' },
      { key: 'password', label: 'Password' },
      { key: 'database', label: 'Database Name' },
      { key: 'port', label: 'Port' },
    ];
    const maskedValue = '***********';
    const showPassword = ref(false);
    const isLoading = ref(false);
    const userCompany = ref('');
    const timer = ref(null);

    const unSubscribedTables = computed(() => {
      const filtered = cloneDeep(tablesMap);
      const selectedTables = credentials.value.tables || [];
      for (const table of selectedTables) {
        delete filtered[table.name];
      }
      return filtered;
    });

    const visibleSubscribedTables = computed(() => (showPassword.value ? credentials.value.tables || [] : []));

    const visibleUnSubscribedTables = computed(() => (showPassword.value ? unSubscribedTables.value : {}));

    const clearRevealTimer = () => {
      if (timer.value) {
        clearTimeout(timer.value);
        timer.value = null;
      }
    };

    const togglePassword = async () => {
      if (showPassword.value) {
        showPassword.value = false;
        clearRevealTimer();
        return;
      }

      isLoading.value = true;
      try {
        const response = await SqlCreds.getSqlCreds(userCompany.value);
        credentials.value = response.data || { tables: [] };
        showPassword.value = true;
        clearRevealTimer();
        timer.value = setTimeout(() => {
          showPassword.value = false;
          toast.open({
            message: 'Timeout. Please view again.',
            type: 'warning',
          });
        }, 120 * 1000);
      } catch (err) {
        toast.open({
          message: 'Failed to retrieve credentials. Please try again later.',
          type: 'error',
        });
      } finally {
        isLoading.value = false;
      }
    };

    onMounted(async () => {
      await store.getters.userPromise;
      userCompany.value = store.state.userData.company;

      if (!userCompany.value) {
        return;
      }

      isLoading.value = true;
      try {
        const response = await SqlCreds.getSqlCreds(userCompany.value);
        credentials.value = response.data || { tables: [] };
      } catch (err) {
        toast.open({
          message: 'Failed to load table subscriptions. Please try again later.',
          type: 'error',
        });
      } finally {
        isLoading.value = false;
      }
    });

    onBeforeUnmount(() => {
      clearRevealTimer();
    });

    return {
      credentials,
      tablesMap,
      unSubscribedTables,
      visibleSubscribedTables,
      visibleUnSubscribedTables,
      credentialFields,
      maskedValue,
      showPassword,
      isLoading,
      togglePassword,
    };
  },
};
</script>

<style scoped>
::v-deep(.box) {
  height: 100%;
  border: 0;
}
</style>